import React, { useCallback, useRef } from 'react';

import DataGrid, {
    Column,
    Editing,
    Popup,
    Form,
    Toolbar, Item as ToolbarItem,
    Lookup } from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/form';
import { withLoadPanel } from '../../../utils/withLoadPanel';
import './CardTasks.scss';
import DataSource from 'devextreme/data/data_source';
import Guid from 'devextreme/core/guid';
import 'devextreme/data/odata/store';
import { CONTRACT_PERIOD_LIST } from '../../../shared/constants';
import { useAuth } from '../../../contexts/auth';
import { useContractTypes } from '../../../contexts/contract-types';
import { createContract, updateContract, deleteContract } from '../../../api/contracts';
import notify from 'devextreme/ui/notify';

const Grid = ({ relationOid }: { relationOid: string }) => {
const { user } = useAuth();
const { contractTypeItems } = useContractTypes();
const gridRef = useRef<DataGrid>(null);
const dataSource = new DataSource({
        store: {
            type: 'odata',
            version: 4,
            key: 'Oid',
            keyType: 'Guid',
            url: `${process.env.REACT_APP_URL}/api/odata/Contract`,
            beforeSend: (e) => {
                e.headers = {
                    'Authorization': `Bearer ${user?.token}`
                };
            },
        },
        paginate: false,
        select: [
            'Oid',
            'ContractType',
            'ContractTypeDefinitionOid',
            'ContractStartDate',
            'ContractEndPeriod',
            'LastMaintenanceDate',
            'LastMaintenanceDate1',
            'AutoRenewContract',
            'ContractPeriod'
        ],
    filter: ['Relation/Oid', '=', new Guid(relationOid)]
});

  const onRowInserting = useCallback((e: any) => {
    e.cancel = (async() => {
      try {
        await createContract({
          RelationOid: relationOid,
          ContractTypeDefinitionOid: e.data.ContractTypeDefinitionOid,
          ContractStartDate: e.data.ContractStartDate,
          ContractPeriod: e.data.ContractPeriod,
          LastMaintenanceDate1: e.data.LastMaintenanceDate1,
          AutoRenewContract: e.data.AutoRenewContract ?? true,
        }, user?.token);
        notify({ message: 'Contract aangemaakt', position: { at: 'bottom center', my: 'bottom center' } }, 'success');
        dataSource.reload();
      } catch (error: any) {
        notify({ message: `Fout bij aanmaken: ${error?.response?.data || error.message}`, position: { at: 'bottom center', my: 'bottom center' } }, 'error', 3000);
      }
    })() as Promise<void>;
  }, [user?.token, relationOid]);

  const onRowUpdating = useCallback((e: any) => {
    const oid = e.key?.toString?.() ?? e.key?._value?.toString?.();
    e.cancel = (async() => {
      try {
        await updateContract(oid, {
          ContractTypeDefinitionOid: e.newData.ContractTypeDefinitionOid ?? e.oldData.ContractTypeDefinitionOid,
          ContractStartDate: e.newData.ContractStartDate ?? e.oldData.ContractStartDate,
          ContractPeriod: e.newData.ContractPeriod ?? e.oldData.ContractPeriod,
          LastMaintenanceDate1: e.newData.LastMaintenanceDate1 ?? e.oldData.LastMaintenanceDate1,
          AutoRenewContract: e.newData.AutoRenewContract ?? e.oldData.AutoRenewContract,
        }, user?.token);
        notify({ message: 'Contract bijgewerkt', position: { at: 'bottom center', my: 'bottom center' } }, 'success');
        dataSource.reload();
      } catch (error: any) {
        notify({ message: `Fout bij bijwerken: ${error?.response?.data || error.message}`, position: { at: 'bottom center', my: 'bottom center' } }, 'error', 3000);
      }
    })() as Promise<void>;
  }, [user?.token]);

  const onRowRemoving = useCallback((e: any) => {
    const oid = e.key?.toString?.() ?? e.key?._value?.toString?.();
    e.cancel = (async() => {
      try {
        await deleteContract(oid, user?.token);
        notify({ message: 'Contract verwijderd', position: { at: 'bottom center', my: 'bottom center' } }, 'success');
        dataSource.reload();
      } catch (error: any) {
        notify({ message: `Fout bij verwijderen: ${error?.response?.data || error.message}`, position: { at: 'bottom center', my: 'bottom center' } }, 'error', 3000);
      }
    })() as Promise<void>;
  }, [user?.token]);

  return (
    <DataGrid
      ref={gridRef}
      className='tasks-grid'
      dataSource={dataSource}
      columnAutoWidth
      onRowInserting={onRowInserting}
      onRowUpdating={onRowUpdating}
      onRowRemoving={onRowRemoving}
      >
          <Toolbar>
              <ToolbarItem name='addRowButton' />
          </Toolbar>
      <Editing
        mode='popup'
        allowUpdating
        allowAdding
        allowDeleting>
            <Popup title='Relatie Info' showTitle width={700} height={525} />
            <Form>
                <Item itemType='group' colCount={2} colSpan={2}>
                <Item dataField='ContractTypeDefinitionOid' editorType='dxSelectBox' label={{ text: 'Contract Type' }} />
                <Item dataField='AutoRenewContract' editorType='dxCheckBox' />
                <Item dataField='ContractStartDate' />
                <Item dataField='LastMaintenanceDate1' />
                <Item dataField='ContractPeriod' editorType='dxSelectBox' />
               </Item>
            </Form>
          </Editing>
    <Column
        visible={false}
        dataField='ContractPeriod'
        hidingPriority={3}>
        <Lookup dataSource={CONTRACT_PERIOD_LIST} />
     </Column>
      <Column
        dataField='ContractTypeDefinitionOid'
        caption='Contract Type'
        hidingPriority={3}>
        <Lookup dataSource={contractTypeItems} valueExpr='value' displayExpr='text' />
     </Column>
      <Column
        dataField='ContractStartDate'
        dataType='date'
        format='dd-MM-yyyy'
        caption='Contract startdatum'
        hidingPriority={1}
       />
      <Column
        format='dd-MM-yyyy'
        dataField='ContractEndPeriod'
        dataType='date'
        caption='Contract einddatum'
        hidingPriority={1}
          />
          <Column
              format='dd-MM-yyyy'
              dataField='LastMaintenanceDate'
              dataType='date'
              caption='Laatste onderhoud'
              hidingPriority={1}
          />
          <Column
              visible={false}
              format='dd-MM-yyyy'
              dataField='LastMaintenanceDate1'
              dataType='date'
              caption='Laatste onderhoud'
              hidingPriority={1}
          />
      <Column
        caption='Auto Contract Verlengen'
        dataField='AutoRenewContract'
        hidingPriority={0}
      />
    </DataGrid>
  );
};

const GridWithLoadPanel = withLoadPanel(Grid);
export const CardTasks = ({ relationOid, isLoading }: { relationOid?: string, isLoading: boolean }) => {
  return (
    <div className='card-tasks'>
          <GridWithLoadPanel
        relationOid={relationOid}
        hasData={!!relationOid}
        loading={isLoading}
        panelProps={{
            container: '.card-tasks',
            position: { of: '.card-tasks' }
        }}
      />
    </div>
  );
};
